import React from 'react';
import { Icon } from '../../../components/ui';
import { usePhone } from '../PhoneContext';

/**
 * DialerFab — a floating phone button that opens the dialer from
 * anywhere in the app. Docked bottom-end (left in the RTL layout) so it
 * doesn't cover the start-side navigation. Hidden while a call is active
 * (the call bar takes over) and until the phone is registered. The DND
 * toggle lives inside the dialer, not here.
 */
export const DialerFab: React.FC = () => {
  const { registration, config, call, openDialer, dialerOpen, dnd, queueWaiting, queueNext, answerNext, reconnect } = usePhone();

  // Don't compete with the call bar / incoming card, or the open dialer.
  if (call || dialerOpen) return null;

  // Lost the connection. Say so instead of vanishing (or, worse, showing a
  // dial button on a dead phone), and offer the reconnect that used to take a
  // logout + login.
  if (config?.configured && (registration === 'reconnecting' || registration === 'failed')) {
    const reconnecting = registration === 'reconnecting';
    return (
      <div className="fixed bottom-4 end-4 z-[1150]">
        <button
          type="button"
          onClick={reconnecting ? undefined : reconnect}
          disabled={reconnecting}
          className={[
            'inline-flex items-center gap-2 px-4 h-11 rounded-full text-sm font-semibold shadow-lg transition',
            reconnecting
              ? 'bg-amber-100 text-amber-800 cursor-default'
              : 'bg-red-600 text-white hover:bg-red-700 hover:-translate-y-0.5',
          ].join(' ')}
          title={reconnecting ? 'המרכזייה מתחברת מחדש' : 'המרכזייה מנותקת — לחץ כדי לחבר מחדש'}
        >
          <Icon name={reconnecting ? 'sync' : 'phone_disabled'} size={18} className={reconnecting ? 'animate-spin' : ''} />
          {reconnecting ? 'מתחבר מחדש…' : 'המרכזייה מנותקת · חבר מחדש'}
        </button>
      </div>
    );
  }

  // Only offer the dialer once we're actually registered.
  if (registration !== 'registered') return null;

  return (
    <div className="fixed bottom-4 end-4 z-[1150] flex flex-col items-end gap-2">
      {/* Queue prompt: callers waiting in the Twilio inbound queue. */}
      {queueWaiting > 0 && (
        <button
          onClick={answerNext}
          className="inline-flex items-center gap-2 px-4 h-11 rounded-full bg-emerald-500 text-white font-semibold text-sm shadow-lg hover:bg-emerald-600 hover:-translate-y-0.5 transition animate-slide-up"
        >
          <Icon name="phone_callback" size={18} />
          <span className="flex flex-col items-start leading-tight">
            <span>ענה לשיחה הבאה</span>
            {/* Who is at the front — resolved server-side when they were enqueued. */}
            {queueNext && (
              <span className="text-[11px] font-normal opacity-90 max-w-[180px] truncate" dir="auto">
                {queueNext.party?.customer?.fullName || queueNext.number}
              </span>
            )}
          </span>
          <span className="min-w-5 h-5 px-1 rounded-full bg-white/25 text-xs flex items-center justify-center">{queueWaiting}</span>
        </button>
      )}
      <button
        onClick={openDialer}
        title="חייגן"
        aria-label="פתח חייגן"
        className="size-14 rounded-full bg-primary text-white shadow-[0_12px_30px_-8px_rgba(37,99,235,0.5)] flex items-center justify-center hover:bg-primary-700 hover:-translate-y-0.5 transition animate-slide-up"
      >
        <Icon name="call" size={24} />
        {/* Presence dot: amber-ish when DND is on, emerald when available. */}
        <span className={`absolute -top-0.5 -end-0.5 size-3 rounded-full border-2 border-white dark:border-slate-900 ${dnd ? 'bg-slate-400' : 'bg-emerald-400'}`} />
      </button>
    </div>
  );
};
